import { apiFetch } from '../utils/api';
import type { ClientModules } from '../utils/clientModules';

export interface CustomerWorkspaceAccess {
  scope?: string;
  orgRole?: string;
  workspaceRole?: string;
  canManageWorkspace?: boolean;
  canManageProjects?: boolean;
  canManageMembers?: boolean;
  paused?: boolean;
}

export interface CustomerWorkspaceSummary {
  _id: string;
  orgId: string;
  name: string;
  slug: string;
  status: 'active' | 'archived';
  notes?: string;
  contactName?: string;
  contactEmail?: string;
  clientModules?: ClientModules;
  projectCount?: number;
  memberCount?: number;
  access?: CustomerWorkspaceAccess;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerWorkspaceMember {
  _id: string;
  workspaceId: string;
  globalUserId: string;
  role: 'client_admin' | 'content_manager' | 'viewer';
  status?: 'active' | 'paused';
  invitedLabel?: string;
  username?: string;
  email?: string;
  joinedAt?: string;
}

export interface CustomerWorkspaceInvite {
  _id: string;
  workspaceId: string;
  email: string;
  role: 'client_admin' | 'content_manager' | 'viewer';
  token: string;
  inviteUrl?: string;
  accessCode?: string;
  qrCode?: string;
  requiresAccessCode?: boolean;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expiresAt?: string;
  createdAt?: string;
}

export interface CustomerWorkspaceProject {
  _id: string;
  name: string;
  slug?: string;
  status: string;
  description?: string;
  customerWorkspaceId?: string;
  publishedAt?: string;
  contentUpdatedAt?: string;
  templateName?: string;
  templateSlug?: string;
  originalTemplateName?: string;
  originalTemplateSlug?: string;
}

export const customerWorkspaceService = {
  list: (): Promise<{ workspaces: CustomerWorkspaceSummary[] }> =>
    apiFetch('/api/saas/v1/customer-workspaces'),
  create: (data: Record<string, unknown>) =>
    apiFetch('/api/saas/v1/customer-workspaces', { method: 'POST', body: JSON.stringify(data) }) as Promise<{ workspace: CustomerWorkspaceSummary }>,
  get: (id: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}`) as Promise<{
      workspace: CustomerWorkspaceSummary;
      access: CustomerWorkspaceAccess;
      projects: CustomerWorkspaceProject[];
      members: CustomerWorkspaceMember[];
      invites?: CustomerWorkspaceInvite[];
    }>,
  update: (id: string, data: Record<string, unknown>) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}`, { method: 'PUT', body: JSON.stringify(data) }) as Promise<{ workspace: CustomerWorkspaceSummary; access: CustomerWorkspaceAccess }>,
  addMember: (id: string, emailOrUsername: string, role: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ emailOrUsername, role }),
    }) as Promise<{ member?: CustomerWorkspaceMember; invite?: CustomerWorkspaceInvite; pending?: boolean }>,
  createInvite: (id: string, email: string, role: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/invites`, {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }) as Promise<{ invite: CustomerWorkspaceInvite }>,
  listInvites: (id: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/invites`) as Promise<{ invites: CustomerWorkspaceInvite[] }>,
  revokeInvite: (id: string, inviteId: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/invites/${inviteId}`, { method: 'DELETE' }) as Promise<{ message?: string }>,
  previewInvite: (token: string) =>
    apiFetch(`/api/saas/v1/customer-workspace-invites/${encodeURIComponent(token)}`) as Promise<{
      workspaceId?: string;
      workspaceName: string;
      email: string;
      role: string;
      status: string;
      requiresAccessCode?: boolean;
    }>,
  verifyInviteAccessCode: (token: string, code: string) =>
    apiFetch(`/api/saas/v1/customer-workspace-invites/${encodeURIComponent(token)}/verify-access-code`, {
      method: 'POST',
      body: JSON.stringify({ code }),
    }) as Promise<{ ok: boolean; requiresAccessCode?: boolean; accessProof: string }>,
  acceptInvite: (token: string, data?: { password?: string; name?: string; accessCode?: string; accessProof?: string }) =>
    apiFetch(`/api/saas/v1/customer-workspace-invites/${encodeURIComponent(token)}/accept`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
    }) as Promise<{ workspaceId: string; member?: CustomerWorkspaceMember }>,
  updateMember: (id: string, memberId: string, updates: { role?: string; status?: 'active' | 'paused' }) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }) as Promise<{ member: CustomerWorkspaceMember }>,
  removeMember: (id: string, memberId: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/members/${memberId}`, { method: 'DELETE' }) as Promise<{ message?: string }>,
  assignProject: (id: string, projectId: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/projects/${projectId}`, { method: 'POST' }) as Promise<{ project: CustomerWorkspaceProject }>,
  unassignProject: (id: string, projectId: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/projects/${projectId}`, { method: 'DELETE' }) as Promise<{ project: CustomerWorkspaceProject }>,
  getActivity: (id: string) =>
    apiFetch(`/api/saas/v1/customer-workspaces/${id}/activity`) as Promise<{ events: Array<Record<string, unknown>> }>,
};
